"use client";

import { useEffect, useState, JSX } from "react";
import Link from "next/link";
import {
  isPossibleToJoin,
  isElevatedRound,
  getPurse,
  getCurrency,
  getSeason4TournamentSubtitle,
  getSeason5TournamentSubtitle,
  getCurrentSeason,
} from "@/utils/tournaments";
import { SeasonTournaments, Tournament } from "@/types/tournament";
import { useUI } from "@/context/UIContext";
import { utcTimeLocal } from "@/utils/time";
import { isNowPlayingTournament } from "@/utils/dateUtils";
import dayjs from "dayjs";
import clsx from "clsx";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import Dropdown, { DropdownSkeleton, Option } from "./Dropdown";
import SeasonSummaryCard from "./SeasonSummaryCard";
import LiveDot from "./LiveDot";
import { sbBlok } from "@/types/sb-types";
import type { SeasonSummaryData } from "@/lib/seasonSummary";
import { getTournamentPath } from "@/utils/tournament-path";

interface ScheduleListProps {
  seasons: Array<SeasonTournaments>;
  seasonSummaries?: Array<sbBlok>;
  currentSeasonSummary?: SeasonSummaryData;
}

enum ScheduleTab {
  LiveUpcoming = "LIVE & UPCOMING",
  Completed = "COMPLETED",
}

interface ConditionalLinkWrapperProps {
  condition: boolean;
  wrapper: (children: React.ReactNode) => JSX.Element;
  children: JSX.Element;
}

const ConditionalLinkWrapper = ({
  condition,
  wrapper,
  children,
}: ConditionalLinkWrapperProps) => (condition ? wrapper(children) : children);

const InProgressBadge = ({ onBrand = false }: { onBrand?: boolean }) => (
  <span
    className={clsx(
      "flex items-center gap-2.25 whitespace-nowrap font-poppins text-xs font-medium uppercase tracking-[0.8px]",
      onBrand ? "text-white" : "text-tm-c-green",
    )}
  >
    <LiveDot onBrand={onBrand} />
    In progress
  </span>
);

// Mirrors the real layout (same wrapper, spacing and row order) so nothing
// shifts when the list hydrates. It renders inside <Schedule>'s grid column,
// so it must not add its own grid-container.
const ScheduleListSkeleton = () => (
  <div aria-busy="true" className="animate-pulse">
    <div className="flex justify-start mt-5 mb-5 mx-0 lg:mb-10">
      <DropdownSkeleton />
    </div>

    <div className="mb-7 md:mb-20 flex flex-nowrap justify-start gap-2.5">
      <div className="h-11.5 w-[187px] rounded-md bg-tm-c-gray-900" />
      <div className="h-11.5 w-[140px] rounded-md bg-tm-c-gray-950" />
    </div>

    <div className="flex items-center justify-between mb-2.5">
      <div className="h-6 w-[120px] rounded bg-tm-c-gray-900" />
      <div className="h-3 w-[70px] rounded bg-tm-c-gray-900" />
    </div>

    <section className="flex flex-col gap-2.5">
      {Array.from(Array(5)).map((_item, i) => (
        <div
          key={i}
          className="w-full h-[160px] lg:h-[100px] 2xl:h-[142px] rounded-[10px] bg-tm-c-gray-950"
        />
      ))}
    </section>
  </div>
);

const ScheduleList = ({
  seasons,
  seasonSummaries,
  currentSeasonSummary,
}: ScheduleListProps) => {
  const { dispatch } = useUI();
  const [isFirstRender, setIsFirstRender] = useState(true);

  // Skip defined-but-empty seasons so an upcoming season isn't offered yet.
  const availableSeasons = [...(seasons ?? [])]
    .filter(({ tournaments }) => tournaments?.length > 0)
    .sort((a, b) => b.season - a.season);

  const seasonOptions: Option[] = availableSeasons.map(({ season }) => ({
    id: season,
    label: `Season ${season}`,
  }));

  const [selectedSeason, setSelectedSeason] = useState<number | undefined>(
    availableSeasons[0]?.season,
  );

  const selectedOption =
    seasonOptions.find(({ id }) => id === selectedSeason) ?? seasonOptions[0];

  const selectedTournaments =
    availableSeasons.find(({ season }) => season === selectedSeason)
      ?.tournaments ?? [];

  const selectedSeasonSummary = seasonSummaries?.find(
    (item) => Number(item?.season_number) === selectedSeason,
  );

  const [activeTab, setActiveTab] = useState<ScheduleTab>(
    ScheduleTab.LiveUpcoming,
  );

  const isCurrentSeason = selectedSeason === getCurrentSeason();

  // Completed and past-season lists show the latest start date first, so the
  // finale is on top. Live & Upcoming keeps chronological order.
  const byLatestStart = (a: Tournament, b: Tournament) =>
    dayjs(b?.startDate).valueOf() - dayjs(a?.startDate).valueOf();

  const completedTournaments = selectedTournaments
    .filter((t) => dayjs(t?.endDate).isBefore(dayjs()))
    .sort(byLatestStart);

  const liveAndUpcomingTournaments = selectedTournaments.filter(
    (t) => !dayjs(t?.endDate).isBefore(dayjs()),
  );

  const hasLiveAndUpcoming = liveAndUpcomingTournaments.length > 0;
  const hasCompleted = completedTournaments.length > 0;

  // Once a season has nothing left to play it is history, so the tabs go away.
  const showTabs = isCurrentSeason && hasLiveAndUpcoming;

  // Never leave a tab selected that has nothing behind it.
  const effectiveTab =
    activeTab === ScheduleTab.Completed && !hasCompleted
      ? ScheduleTab.LiveUpcoming
      : activeTab;

  // Without tabs the list is history: a past season, or a finished current one.
  const isShowingCompleted = showTabs
    ? effectiveTab === ScheduleTab.Completed
    : true;

  // Past seasons show the whole season in one list; the tabs are
  // current-season behaviour only.
  const visibleTournaments = !isCurrentSeason
    ? [...selectedTournaments].sort(byLatestStart)
    : isShowingCompleted
      ? completedTournaments
      : liveAndUpcomingTournaments;

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isJoinAvailable = (tournament: Tournament) => {
    if (!tournament.signUpStartTime || !tournament.signUpEndTime) return false;

    const signupStart = dayjs(tournament.signUpStartTime);
    const signupEnd = dayjs(tournament.signUpEndTime);
    const currentDate = dayjs();
    return (
      tournament?.versionInfo?.state !== "DRAFT" &&
      currentDate.isAfter(signupStart) &&
      currentDate.isBefore(signupEnd) &&
      isPossibleToJoin(
        tournament?.participants?.allInvitationsCount -
          tournament?.participants?.declinedCount -
          tournament?.participants?.withdrawnCount,
      )
    );
  };

  if (isFirstRender) return <ScheduleListSkeleton />;

  return (
    <div>
      <div className="flex justify-start mt-5 mb-5 mx-0 lg:mb-10">
        <Dropdown
          defaultValue={selectedOption}
          value={selectedOption}
          onChange={(option: Option) => setSelectedSeason(Number(option.id))}
          options={seasonOptions}
        />
      </div>

      {selectedSeasonSummary && (
        <div className="mt-2.5 mb-12 xl:mb-25">
          <SeasonSummaryCard
            key={selectedSeasonSummary._uid}
            blok={selectedSeasonSummary}
            currentSeasonData={currentSeasonSummary}
          />
        </div>
      )}

      {showTabs && (
        <div className="mb-7 md:mb-20 flex flex-nowrap justify-start gap-2.5">
          {Object.values(ScheduleTab).map((tab: ScheduleTab) => {
            const isDisabled = tab === ScheduleTab.Completed && !hasCompleted;
            const isActive = effectiveTab === tab;

            return (
              <button
                key={tab}
                type="button"
                disabled={isDisabled}
                onClick={() => setActiveTab(tab)}
                className={clsx(
                  `inline-flex font-poppins py-2.5 px-5 items-center justify-center whitespace-nowrap rounded-md text-[15px] font-normal uppercase transition-colors duration-300 ease-in-out`,
                  {
                    "text-tm-c-brand bg-tm-c-gray-900": isActive && !isDisabled,
                    "text-tm-c-gray-500 bg-tm-c-gray-950 hover:cursor-pointer hover:text-white hover:bg-tm-c-gray-900":
                      !isActive && !isDisabled,
                    "text-tm-c-gray-500 bg-tm-c-gray-950 cursor-not-allowed opacity-60 pointer-events-none":
                      isDisabled,
                  },
                )}
              >
                {tab}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mb-2.5 font-poppins">
        <h2 className="text-base xl:text-xl m-0">
          {isShowingCompleted ? "Tournament History" : "Tournaments"}
        </h2>
        <span className="text-xs text-tm-c-gray-500">
          {`${visibleTournaments.length} Event${
            visibleTournaments.length === 1 ? "" : "s"
          }`}
        </span>
      </div>

      <section className="flex flex-col gap-2.5">
        {visibleTournaments?.map(
          (
            tournament: Tournament & {
              isRange?: boolean;
            },
          ) => {
            const playersCount =
              tournament?.participants?.allInvitationsCount -
              tournament?.participants?.declinedCount -
              tournament?.participants?.withdrawnCount;

            const signupUri =
              !!tournament?.locationsConfiguration?.length &&
              tournament?.locationsConfiguration[0].paymentUrl;

            const isElevated = isElevatedRound(
              tournament.season,
              tournament.name,
            );

            const signUpStart = tournament.signUpStartTime;
            const signUpEnd = tournament.signUpEndTime;
            const canJoin = isJoinAvailable(tournament) && !!signupUri;

            // Playing and entry closed; mutually exclusive with Join.
            const isInProgress =
              !canJoin &&
              isNowPlayingTournament(tournament.startDate, tournament.endDate);

            const isPublishedTournament =
              tournament?.versionInfo?.state &&
              tournament?.versionInfo?.state !== "DRAFT";

            const isNextTourChampionship =
              (tournament.season === 4 || tournament.season === 5) &&
              tournament.name
                ?.toLowerCase()
                ?.includes("next golf tour championship");

            let tournamentSubtitle: string | null = null;
            if (tournament.season === 4) {
              tournamentSubtitle = getSeason4TournamentSubtitle(
                tournament.startDate,
                tournament.endDate,
              );
            } else if (tournament.season === 5) {
              tournamentSubtitle = getSeason5TournamentSubtitle(
                tournament.name,
              );
            }

            const isNextWorldSeriesCard =
              tournamentSubtitle === "NEXT World Series";

            return (
              <div className="bg-tm-c-dark" key={tournament.id}>
                <div
                  className={clsx(
                    `
                    relative rounded-[10px]              
                    p-6 2xl:pr-10 2xl:pl-0 2xl:py-0 cursor-default
                    transition-colors duration-300 ease-in-out text-white
                `,
                    {
                      "bg-gradient-to-r from-[#616161]/20 to-tm-c-gray-950 2xl:!bg-none 2xl:!bg-tm-c-gray-950 2xl:hover:!bg-tm-c-gray-900":
                        !isNextWorldSeriesCard && !isNextTourChampionship,
                      "cursor-pointer": isPublishedTournament,
                      "bg-gradient-to-r from-tm-c-brand/80 to-tm-c-brand/50 2xl:!bg-none 2xl:!bg-tm-c-brand/50 2xl:hover:!bg-tm-c-brand/70":
                        isNextWorldSeriesCard,
                      "bg-tm-c-brand 2xl:!bg-tm-c-brand/90 2xl:hover:!bg-tm-c-brand/100":
                        isNextTourChampionship,
                      "opacity-40 !pointer-events-none": !isPublishedTournament,
                    },
                  )}
                >
                  <ConditionalLinkWrapper
                    condition={!!isPublishedTournament}
                    wrapper={(
                      children: React.ReactNode | React.ReactNode[],
                    ) => (
                      <Link
                        className="hover:!opacity-100"
                        href={getTournamentPath(tournament)}
                      >
                        {children}
                      </Link>
                    )}
                  >
                    <div className="lg:flex lg:flex-row lg:items-center lg:w-full lg:gap-4">
                      <div className="flex flex-col lg:w-2/3 2xl:flex-row 2xl:items-center 2xl:gap-[70px] ">
                        <div
                          className={clsx(
                            "2xl:rounded-l-[10px] 2xl:bg-tm-c-dark",
                            {
                              "2xl:!bg-tm-c-brand": isNextTourChampionship,
                            },
                          )}
                        >
                          <div
                            className={clsx(
                              `
                          text-[15px] lg:text-xs 2xl:text-base
                          2xl:p-7 2xl:rounded-l-[10px] 2xl:min-w-[225px]
                          2xl:flex 2xl:flex-col 2xl:justify-center 2xl:items-center
                          2xl:bg-tm-c-gray-900
                        `,
                              {
                                "2xl:!bg-tm-c-brand/80": isNextWorldSeriesCard,
                                "2xl:!bg-tm-c-brand": isNextTourChampionship,
                              },
                            )}
                          >
                            <span>
                              {utcTimeLocal(
                                tournament.startDate,
                                "MMM. D, H:mm z",
                              )}
                            </span>
                            {" - "}
                            <span>
                              {utcTimeLocal(
                                tournament.endDate,
                                "MMM. D, H:mm z",
                              )}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div
                            className={clsx(
                              `flex items-center gap-2.5 mt-1
                            text-xl w-full justify-between
                          `,
                              {
                                "font-bold": isNextTourChampionship,
                              },
                            )}
                          >
                            <div className="max-w-[300px] md:max-w-[400px]">
                              {tournament.name}
                            </div>
                          </div>
                          {!!tournamentSubtitle && (
                            <div className="text-xs opacity-50 mt-1">
                              {tournamentSubtitle}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-row w-full text-xs lg:w-[35%] lg:items-center 2xl:w-[50%] 2xl:text-xl">
                        {!tournament.isRange && (
                          <div
                            className={clsx(
                              "w-[35%] text-xs 2xl:mr-16 mt-6 lg:mt-0",
                              {
                                "[&>*]:text-tm-c-brand": isElevated,
                              },
                            )}
                          >
                            <div className="2xl:text-base 2xl:text-tm-c-light">
                              Purse
                            </div>
                            <div className="2xl:text-xl">
                              {`${getCurrency(
                                tournament.season,
                                tournament.name,
                              )}${getPurse(
                                tournament.season,
                                tournament.id,
                                tournament.name,
                                playersCount,
                              ).toLocaleString("en-US")}`}
                            </div>
                          </div>
                        )}
                        {!!tournament.winner && (
                          <div className="mt-6 lg:mt-0 text-xs">
                            <div className="flex items-center gap-0.5 2xl:gap-1 2xl:text-base 2xl:text-tm-c-light">
                              Winner
                              <EmojiEventsOutlinedIcon
                                aria-hidden="true"
                                sx={{ fontSize: 14 }}
                              />
                            </div>
                            <div className="2xl:text-xl">
                              {tournament.winner}
                            </div>
                          </div>
                        )}
                        {/* Mobile slot: badge and signup icons share one spot,
                            right-aligned and centred on the Purse block. */}
                        <div className="lg:hidden ml-auto mt-6 flex items-center pr-1">
                          {isInProgress && (
                            <InProgressBadge
                              onBrand={
                                isNextWorldSeriesCard || isNextTourChampionship
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </ConditionalLinkWrapper>
                  {/* Mobile join control: a real button outside the card Link so
                      it is keyboard-focusable and not nested in the anchor. */}
                  {canJoin && (
                    <button
                      type="button"
                      aria-label={
                        signUpStart && signUpEnd
                          ? `Join ${tournament.name}, sign up ${dayjs(signUpStart).format("MMM D")} to ${dayjs(signUpEnd).format("MMM D")}`
                          : `Join ${tournament.name}`
                      }
                      className="lg:hidden absolute bottom-6 right-6 flex items-center cursor-pointer text-tm-c-brand"
                      onClick={() => {
                        window.localStorage.setItem(
                          "next_signup_uri",
                          signupUri as string,
                        );
                        dispatch({
                          type: "toggleJoinFlowInfoModal",
                          payload: {
                            isOpen: true,
                            title: tournament.name,
                            modalType: "signup",
                          },
                        });
                      }}
                    >
                      <span className="flex flex-col items-end gap-1.5 text-right font-poppins text-xs">
                        <span className="font-medium">Sign up</span>
                        {!!signUpStart && !!signUpEnd && (
                          <span className="font-normal text-white/50 whitespace-nowrap">
                            {`(${dayjs(signUpStart).format("MMM D")} - ${dayjs(signUpEnd).format("MMM D")})`}
                          </span>
                        )}
                      </span>
                    </button>
                  )}
                  {/* In progress and Join share the right-aligned status slot. */}
                  <div className="hidden lg:flex items-center absolute top-0 bottom-0 my-auto right-6 2xl:right-10 h-fit pointer-events-none">
                    {isInProgress && (
                      <InProgressBadge
                        onBrand={
                          isNextWorldSeriesCard || isNextTourChampionship
                        }
                      />
                    )}
                    {canJoin && (
                      <button
                        type="button"
                        className="pointer-events-auto cursor-pointer text-tm-c-brand"
                        onClick={() => {
                          window.localStorage.setItem(
                            "next_signup_uri",
                            signupUri as string,
                          );
                          dispatch({
                            type: "toggleJoinFlowInfoModal",
                            payload: {
                              isOpen: true,
                              title: tournament.name,
                              modalType: "signup",
                            },
                          });
                        }}
                      >
                        <span className="flex flex-col items-end gap-1.5 text-right font-poppins">
                          <span className="text-base font-normal">Sign up</span>
                          {!!signUpStart && !!signUpEnd && (
                            <span className="text-xs font-normal text-white/50 whitespace-nowrap">
                              {`(${dayjs(signUpStart).format("MMM D")} - ${dayjs(signUpEnd).format("MMM D")})`}
                            </span>
                          )}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          },
        )}
      </section>
    </div>
  );
};

export default ScheduleList;
